import { PlayerStatus } from './PlayerStatus'

export enum Message {
  UPDATE_PLAYER,
  UPDATE_PLAYER_NAME,
  READY_TO_CONNECT,
  DISCONNECT_STREAM,
  CONNECT_TO_WHITEBOARD,
  DISCONNECT_FROM_WHITEBOARD,
  VIDEO_CONNECTED,
  VIDEO_DISCONNECTED,
  ADD_CHAT_MESSAGE,
  ADD_REACTION,
  SEND_ROOM_DATA,
  KICK_USER,
  PLAYER_LEFT,
  PLAYER_JOINED,
  SEND_DIRECT_MESSAGE,
  DIRECT_MESSAGE_RECEIVED,
  DIRECT_MESSAGE_ERROR,
  DIRECT_MESSAGE_SENT,
  RTC_SIGNAL,
  RTC_SCREEN_SIGNAL,
  CLEANUP_PHANTOM_SESSION,
  REQUEST_ROOM_STATE,
  REQUEST_PLAYER_INFO,
  PROXIMITY_UPDATE,
  UPDATE_CONNECTION_STATUS,
  ITEM_USER_ADDED,
  ITEM_USER_REMOVED,
  STOP_SCREEN_SHARE,
  LIVEKIT_TOKEN,
  REQUEST_LIVEKIT_TOKEN,
  LIVEKIT_ERROR,
  EXISTING_PLAYERS,
  VIEWPORT_UPDATE, // client reports camera bounds to server for AoI filtering
  PLAYER_STATUS_CHANGE = 'player-status-change',
  PLAYER_STATUS_TEXT_CHANGE = 'player-status-text-change',
  SPAWN_STRESS_BOTS = 'spawn-stress-bots',
  REMOVE_STRESS_BOTS = 'remove-stress-bots',
  STRESS_BOT_STATUS = 'stress-bot-status',
  REQUEST_STRESS_BOT_STATUS = 'request-stress-bot-status',
  REQUEST_SCREEN_SHARE_TAKEOVER = 'request-screen-share-takeover',
  SCREEN_SHARE_TAKEOVER_REQUEST = 'screen-share-takeover-request',
  SCREEN_SHARE_TAKEOVER_RESPONSE = 'screen-share-takeover-response',
  SCREEN_SHARE_TAKEOVER_RESULT = 'screen-share-takeover-result',
  START_SCREEN_SHARE_SESSION = 'start-screen-share-session',
  END_SCREEN_SHARE_SESSION = 'end-screen-share-session',
  // Virtual room ownership and password
  SET_VIRTUAL_ROOM_PASSWORD = 'set-virtual-room-password',
  REMOVE_VIRTUAL_ROOM_PASSWORD = 'remove-virtual-room-password',
  ENTER_VIRTUAL_ROOM_PASSWORD = 'enter-virtual-room-password',
  LEAVE_VIRTUAL_ROOM = 'leave-virtual-room',
  PLAYER_VIRTUAL_ROOM_CHANGED = 'player-virtual-room-changed',
  VIRTUAL_ROOM_FILTER_STATE = 'virtual-room-filter-state',
  VIRTUAL_ROOM_OCCUPANTS = 'virtual-room-occupants',
  KNOCK_VIRTUAL_ROOM = 'knock-virtual-room',
  VIRTUAL_ROOM_KNOCK_SOUND = 'virtual-room-knock-sound',
  VIRTUAL_ROOM_KNOCK_REQUEST = 'virtual-room-knock-request',
  ALLOW_VIRTUAL_ROOM_KNOCK = 'allow-virtual-room-knock',
  DENY_VIRTUAL_ROOM_KNOCK = 'deny-virtual-room-knock',
  VIRTUAL_ROOM_KNOCK_DENIED = 'virtual-room-knock-denied',
  TRANSFER_VIRTUAL_ROOM_OWNERSHIP = 'transferVirtualRoomOwnership',
  RAISE_HAND = 'raise-hand',
  KICK_BY_DUPLICATE_LOGIN = 'kick-by-duplicate-login',
  AUDIO_CONNECTED = 'audio-connected',
  AUDIO_DISCONNECTED = 'audio-disconnected',
  // Follow / Escort
  FOLLOW_START = 'follow-start',
  FOLLOW_STOP = 'follow-stop',
  FOLLOW_STARTED = 'follow-started',
  FOLLOW_STOPPED = 'follow-stopped',
  FOLLOW_REJECTED = 'follow-rejected',
  FOLLOW_CONFIRMED = 'follow-confirmed',
  // Teleport (room and player targets — see SC-393/394)
  TELEPORT_REQUEST = 'teleport-request',
  TELEPORTED = 'teleported',
  TELEPORT_DENIED = 'teleport-denied',
  // Teleport DND notify-and-wait ping (see SC-394)
  TELEPORT_PING = 'teleport-ping',
  TELEPORT_PING_RESPONSE = 'teleport-ping-response',
  TELEPORT_PING_SENT = 'teleport-ping-sent',
  // SC-425: a requester has one pending DND ping at a time; the earlier one is cancelled.
  TELEPORT_PING_CANCELLED = 'teleport-ping-cancelled',
  TELEPORT_PING_SUPERSEDED = 'teleport-ping-superseded',
  // SC-425: client nudged its landing off a collidable object; server validates + applies.
  TELEPORT_LANDING_ADJUSTED = 'teleport-landing-adjusted',
  // Observer-side arrival fade broadcast (see SC-394)
  PLAYER_TELEPORTED = 'player-teleported',
  // External guest
  GUEST_ARRIVED = 'guest-arrived',
  GUEST_ADMIT = 'guest-admit',
  GUEST_ADMIT_ALL = 'guest-admit-all',
  ESCORT_START = 'escort-start',
  ESCORT_ARRIVED = 'escort-arrived',
  END_MEETING_GUESTS = 'end-meeting-guests',
  GUEST_REMOVE = 'guest-remove',
  GUEST_FAREWELL = 'guest-farewell',
  GUEST_WAITING = 'guest-waiting',
  GUEST_TELEPORTED = 'guest-teleported',
  PLAYER_TELEPORT = 'player-teleport',
  /** Follower → server: tab hidden/visible, so the server takes over or hands back follow movement. */
  FOLLOW_BACKGROUND = 'follow-background',
  /** Server → follower: authoritative position to adopt when handing follow back to the client. */
  FOLLOW_RESYNC = 'follow-resync',
  // Room booking
  ROOM_BOOKING_INFO = 'room-booking-info',
  ROOM_BOOKING_REMINDER = 'room-booking-reminder',
  ROOM_BOOKING_INVITE_RECEIVED = 'room-booking-invite-received',
  ROOM_BOOKING_INVITE_ACTION = 'room-booking-invite-action',
  ROOM_BOOKING_WRAP_UP = 'room-booking-wrap-up',
  // On-demand full roster for the teleport picker's People tab (SC-397 phase 2) — deliberately
  // not AoI-filtered, unlike the client's own local `network.players` mirror.
  TELEPORT_PEOPLE_LIST_REQUEST = 'teleport-people-list-request',
  TELEPORT_PEOPLE_LIST = 'teleport-people-list',
}

export interface IPlayerStatusChange {
  status: PlayerStatus
}

export interface IPlayerStatusTextChange {
  statusText: string
  statusIcon?: string
}

// Teleport. Discriminated union on target.type — room-target (SC-393/394) and
// player-target (SC-394) share this one resolution/message shape.
export type TeleportTarget = { type: 'room'; roomId: string } | { type: 'player'; sessionId: string }

export interface ITeleportRequest {
  target: TeleportTarget
}

export interface ITeleported {
  x: number
  y: number
  anim?: string
  roomId?: string
  targetSessionId?: string
  /** SC-425: monotonically increasing per traveler; echoed by ITeleportLandingAdjusted. */
  epoch?: number
  /** Swept along by the leader's teleport — the follow survives, unlike the traveler's own teleport. */
  viaFollow?: boolean
}

export type TeleportDenyReason =
  | 'disabled'
  | 'rate_limited'
  | 'room_not_found'
  | 'target_not_found'
  | 'dnd'
  // SC-436: the pinged target left or dropped mid-wait — distinct from a decline/timeout.
  | 'target_unavailable'
  | 'self'
  // SC-425: no-op teleports (see types/teleportEligibility.ts)
  | 'same_room'
  | 'too_close'
  // External guests are zone-restricted; only their host moves them (escort / admit).
  | 'guest'
  // The requester is escorting guests; teleporting would drag them out of the escort.
  | 'escorting'

export interface ITeleportDenied {
  reason: TeleportDenyReason
  roomId?: string
  targetSessionId?: string
}

// DND notify-and-wait: 'dnd' above is a terminal outcome only (explicit decline or 60s timeout;
// the target disconnecting mid-wait is 'target_unavailable') — never the immediate reply to TELEPORT_REQUEST.
export interface ITeleportPing {
  requesterSessionId: string
  requesterName: string
}

export interface ITeleportPingResponse {
  accepted: boolean
  /** Lets the server ignore a response to a ping that has since been cancelled/replaced. */
  requesterSessionId?: string
}

/** Server → earlier DND target: the requester moved on, close the Allow/Not now toast. */
export interface ITeleportPingCancelled {
  requesterSessionId: string
}

/** Server → requester: your earlier ping to this target was replaced by a newer request. */
export interface ITeleportPingSuperseded {
  targetSessionId: string
  targetName?: string
}

/** Client → server: landing nudged to the nearest free point; `epoch` echoes ITeleported.epoch. */
export interface ITeleportLandingAdjusted {
  x: number
  y: number
  epoch: number
}

export interface ITeleportPingSent {
  targetSessionId: string
  targetName?: string
}

// Observer-side arrival fade: broadcast to everyone except the traveler so onlookers can play a
// brief fade-in cue; the schema patch already carries the authoritative x/y.
export interface IPlayerTeleported {
  sessionId: string
  x: number
  y: number
}

// Full roster for the teleport picker's People tab (SC-397 phase 2) — read directly off
// this.state.players server-side, never AoI-filtered, so "who can I teleport to" doesn't
// silently shrink in a large room or with full-lobby-sync disabled.
export interface ITeleportPeopleList {
  // roomId/x/y (SC-425) let the client disable redundant rows for people outside its viewport.
  people: Array<{ sessionId: string; name: string; userId: string; roomId?: string; x?: number; y?: number }>
}
